import { Switch } from '@advanza/advanza_generic'
import { Button, Divider, Icon, LoadingDots, PreIcon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import clsx from 'clsx'
import { useToast } from 'components/misc/Toaster'
import ActivateSubscriptionModal from 'components/providers/ActivateSubscriptionModal'
import AddPresenceDiscountModal from 'components/providers/AddPresenceDiscountModal'
import AddPresenceRolloverDiscountModal from 'components/providers/AddPresenceRolloverDiscountModal'
import DeactivateSubscriptionModal from 'components/providers/DeactivateSubscriptionModal'
import DeleteDiscountModal from 'components/providers/DeleteDiscountModal'
import PresenceDiscountList from 'components/providers/PresenceDiscountList'
import PresenceSubscriptionHistoryModal from 'components/providers/PresenceSubscriptionHistoryModal'
import { DISCOUNT_STATUS, SUBSCRIPTION_STATUS, usePresenceSubscription } from 'hooks/providerHooks'
import {
    ProvidersPresenceDiscount,
    SOURCE_GENERAL,
    STATUS_ON_HOLD,
    STATUS_PENDING,
} from 'interfaces/PresenceSubscription'
import { Provider } from 'interfaces/Providers'
import { useEffect, useState } from 'react'

/**
 * `call()` rejects with the parsed JSON error body for non-success responses
 * (shape `{ status: 'error', message?, errors? }`) and only rejects with a real
 * Error on network failures. So `error instanceof Error` would mask the API
 * message; pull it off the payload instead.
 */
function extractApiErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error) {
        return error.message
    }
    if (typeof error === 'object' && error !== null) {
        const message = (error as { message?: unknown }).message
        if (typeof message === 'string' && message.length > 0) {
            return message
        }
    }
    return fallback
}

/**
 * PresenceSubscriptionSettings component
 *
 * Handles presence subscription settings including discounts.
 *
 * Features:
 * - Shows effective discount amount (capped at subscription fee to prevent negative values)
 * - Displays warnings when total discount exceeds subscription fee
 * - Visual indicators for discounts contributing to over-discounting
 * - Capping information shown below total subscription price when applicable
 */
interface PresenceSubscriptionSettingsProps {
    provider: Provider
}

const PresenceSubscriptionSettings = ({ provider }: PresenceSubscriptionSettingsProps) => {
    const { addToast } = useToast()

    const {
        subscription,
        amount,
        overrideAmount,
        totalDiscount,
        totalFee,
        allowPresenceSubscription,
        error: presenceSubscriptionError,
        isLoading,
        updateSubscription,
        addSubscriptionDiscount,
        deleteSubscriptionDiscount,
        addSubscriptionCharges,
    } = usePresenceSubscription(provider.service_provider_id)

    // Check if provider is basic (not signed up for premium)
    const isBasicProvider = !provider.isSigned

    // Check if we have a pending or on_hold subscription
    const hasPendingSubscription = subscription?.status === STATUS_PENDING
    const hasOnHoldSubscription = subscription?.status === STATUS_ON_HOLD
    const hasPendingOrOnHoldSubscription = hasPendingSubscription || hasOnHoldSubscription

    // Check if we have any subscription (pending or active)
    const hasAnySubscription = subscription && subscription.status

    // Helper function to calculate effective discount (capped at subscription amount)
    const getEffectiveDiscountInfo = () => {
        const baseAmount = overrideAmount ?? amount ?? 0
        const effectiveDiscount = Math.min(totalDiscount, baseAmount)
        const isOverDiscounted = totalDiscount > baseAmount

        return {
            baseAmount,
            effectiveDiscount,
            isOverDiscounted,
            originalDiscount: totalDiscount,
        }
    }

    // Helper function to convert database dates to effective billing months for display
    const getEffectiveBillingMonth = (date: string | null) => {
        if (!date) return null
        const dateObj = new Date(date)
        return dateObj.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    }

    const [statusSwitchState, setStatusSwitchState] = useState(
        subscription?.status === SUBSCRIPTION_STATUS.ACTIVE,
    )

    useEffect(() => {
        setStatusSwitchState(subscription?.status === SUBSCRIPTION_STATUS.ACTIVE)
    }, [subscription?.status])

    const handleSubscriptionToggle = async () => {
        const newStatusSwitchState = !statusSwitchState

        if (newStatusSwitchState) {
            await NiceModal.show(ActivateSubscriptionModal, {
                onConfirm: async (overrideAmount) => {
                    try {
                        await updateSubscription({
                            status: SUBSCRIPTION_STATUS.ACTIVE,
                            start_date: new Date().toISOString(),
                            subscription_override_amount: overrideAmount,
                        })
                        setStatusSwitchState(true)
                        addToast({
                            msg: 'Presence subscription activated successfully',
                            green: true,
                            icon: 'check_circle',
                        })
                    } catch (error) {
                        setStatusSwitchState(false)
                        addToast({
                            msg: extractApiErrorMessage(
                                error,
                                'Failed to activate presence subscription',
                            ),
                            red: true,
                            icon: 'error',
                        })
                    }
                },
                onCancel: () => setStatusSwitchState(false),
            })
        } else {
            await NiceModal.show(DeactivateSubscriptionModal, {
                onConfirm: async () => {
                    try {
                        await updateSubscription({
                            status: SUBSCRIPTION_STATUS.INACTIVE,
                        })
                        setStatusSwitchState(false)
                        addToast({
                            msg: 'Presence subscription deactivated successfully',
                            green: true,
                            icon: 'check_circle',
                        })
                    } catch (error) {
                        setStatusSwitchState(true)
                        addToast({
                            msg: extractApiErrorMessage(
                                error,
                                'Failed to deactivate presence subscription',
                            ),
                            red: true,
                            icon: 'error',
                        })
                    }
                },
                onCancel: () => setStatusSwitchState(true),
            })
        }
    }

    const addDiscount = async () => {
        await NiceModal.show(AddPresenceDiscountModal, {
            onConfirm: async (data) => {
                if (!subscription) return

                const newDiscount: ProvidersPresenceDiscount = {
                    service_provider_presence_discount_id: 0, // This will be set by the backend
                    service_provider_presence_subscription_id:
                        subscription.service_provider_presence_subscription_id,
                    type: data.type,
                    amount: data.amount,
                    start_date: data.start_date,
                    end_date: data.end_date,
                    status: DISCOUNT_STATUS.ACTIVE,
                    source: SOURCE_GENERAL, // Default source for manually added discounts
                }

                try {
                    await addSubscriptionDiscount(newDiscount)
                    addToast({
                        msg: 'Discount added successfully',
                        green: true,
                        icon: 'check_circle',
                    })
                } catch (error) {
                    addToast({
                        msg: extractApiErrorMessage(error, 'Failed to add discount'),
                        red: true,
                        icon: 'error',
                    })
                }
            },
            onCancel: () => {},
        })
    }

    const deleteDiscount = async (discountId: number) => {
        await NiceModal.show(DeleteDiscountModal, {
            onConfirm: async () => {
                try {
                    await deleteSubscriptionDiscount(discountId)
                    addToast({
                        msg: 'Discount deleted successfully',
                        green: true,
                        icon: 'check_circle',
                    })
                } catch (error) {
                    addToast({
                        msg: extractApiErrorMessage(error, 'Failed to delete discount'),
                        red: true,
                        icon: 'error',
                    })
                }
            },
            onCancel: () => {},
        })
    }

    const addPresenceSubscriptionCharges = async () => {
        try {
            const response = await addSubscriptionCharges()
            addToast({ msg: response.message, green: true, icon: 'attach_money' })
        } catch (error: any) {
            addToast({
                msg:
                    error.error ||
                    'An error occurred while adding charges. Charge may already exist.',
                red: true,
                icon: 'money_off',
            })
        }
    }

    const addRolloverDiscount = async () => {
        await NiceModal.show(AddPresenceRolloverDiscountModal, {
            providerId: provider.service_provider_id,
        })
    }

    // Render basic provider with no pending subscription
    const renderBasicProviderNoSubscription = () => {
        return (
            <div>
                {renderSubscriptionHeader()}
                <div className="mb-4">
                    <p className="text-neutral-600 mb-4">
                        Provider has not signed up for premium. You can add discount offers that
                        will be applied when they upgrade.
                        {amount && (
                            <span className="block mt-2 font-medium text-neutral-800">
                                Monthly subscription price:{' '}
                                <span className="font-bold">€{amount.toFixed(2)}</span>
                            </span>
                        )}
                    </p>
                </div>
                <div className="mb-4">
                    <Button name="text" onClick={addRolloverDiscount}>
                        <PreIcon primColor icon="add">
                            Add Discount Offer
                        </PreIcon>
                    </Button>
                </div>
            </div>
        )
    }

    // Render subscription header with status and controls
    const renderSubscriptionHeader = () => {
        return (
            <div className="absolute top-[-20px] right-0 flex items-center gap-3">
                <Button
                    name="text"
                    onClick={() =>
                        NiceModal.show(PresenceSubscriptionHistoryModal, {
                            providerId: provider.service_provider_id,
                        })
                    }
                    className="text-md text-neutral-600 hover:text-neutral-800">
                    <PreIcon primColor icon="history">
                        History
                    </PreIcon>
                </Button>
                {hasPendingOrOnHoldSubscription ? (
                    <div className="flex items-center gap-2 text-tertiary-500">
                        <Icon name="timer" size="sm" orange />
                        <span className="text-md font-bold">
                            {hasOnHoldSubscription ? 'On Hold' : 'Pending'}
                        </span>
                    </div>
                ) : allowPresenceSubscription ? (
                    <Switch
                        checked={statusSwitchState}
                        onChange={handleSubscriptionToggle}></Switch>
                ) : null}
            </div>
        )
    }

    // Determine what to render based on clear conditions
    const renderContent = () => {
        if (isLoading) {
            return <LoadingDots />
        }

        if (presenceSubscriptionError) {
            return <p className="text-red-600">{presenceSubscriptionError}</p>
        }

        if (isBasicProvider && !hasAnySubscription) {
            return renderBasicProviderNoSubscription()
        }

        if (subscription && subscription.status) {
            // Has subscription (active, inactive, or pending)
            return (
                <div>
                    {renderSubscriptionHeader()}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="font-bold">
                            <div className="mb-1">Status:</div>
                            <div className="mb-1">Subscription Start Date:</div>
                            <div className="mb-1">Subscription Fee:</div>
                            <div className="mb-1">Subscription Fee Override:</div>
                            <div className="mb-1">Discounted Price:</div>
                            <div className="mb-1 mt-2">Total Subscription Price:</div>
                            <div className="text-sm text-grey-700 font-normal ml-2">
                                (
                                {new Date(
                                    new Date().getFullYear(),
                                    new Date().getMonth() + 1,
                                    1,
                                ).toLocaleDateString('en-GB', {
                                    month: 'long',
                                    year: 'numeric',
                                })}{' '}
                                billing)
                            </div>
                        </div>
                        <div>
                            <div className="mb-1">
                                {subscription.status === SUBSCRIPTION_STATUS.ACTIVE ? (
                                    <span className="text-success font-bold">
                                        {subscription.status.charAt(0).toUpperCase() +
                                            subscription.status.slice(1)}
                                    </span>
                                ) : subscription.status === STATUS_PENDING ||
                                  subscription.status === STATUS_ON_HOLD ? (
                                    <span className="text-tertiary-500 font-bold">
                                        {subscription.status === STATUS_ON_HOLD
                                            ? 'On Hold'
                                            : subscription.status.charAt(0).toUpperCase() +
                                              subscription.status.slice(1)}
                                    </span>
                                ) : (
                                    <span className="text-error font-bold">
                                        {subscription.status.charAt(0).toUpperCase() +
                                            subscription.status.slice(1)}
                                    </span>
                                )}{' '}
                                [<span className="text-italic">{subscription.service_name}</span>]
                            </div>
                            <div className="mb-1">
                                {subscription.start_date
                                    ? new Date(subscription.start_date).toLocaleDateString('en-GB')
                                    : '-'}
                            </div>
                            <div className="mb-1">€{amount?.toFixed(2)}</div>
                            <div className="mb-1">
                                {overrideAmount ? '€' + Number(overrideAmount).toFixed(2) : '-'}
                            </div>
                            <div className="mb-1">
                                {subscription.presence_discounts &&
                                subscription.presence_discounts.length > 0
                                    ? (() => {
                                          const discountInfo = getEffectiveDiscountInfo()

                                          return (
                                              <div>
                                                  <span
                                                      className={clsx({
                                                          'text-tertiary-800':
                                                              discountInfo.isOverDiscounted,
                                                          'font-bold': true,
                                                      })}>
                                                      €{discountInfo.effectiveDiscount.toFixed(2)}
                                                  </span>
                                              </div>
                                          )
                                      })()
                                    : 'No discount applied'}
                            </div>
                            <div className="flex mb-1 mt-2">
                                <span
                                    className={clsx('font-bold text-white p-1 rounded-md', {
                                        'bg-success':
                                            subscription.status === SUBSCRIPTION_STATUS.ACTIVE,
                                        'bg-error':
                                            subscription.status === SUBSCRIPTION_STATUS.INACTIVE,
                                    })}>
                                    €{totalFee ? totalFee.toFixed(2) : '0.00'}
                                </span>
                                {subscription.status === SUBSCRIPTION_STATUS.ACTIVE && (
                                    <span className="ml-4">
                                        <Button
                                            name="text"
                                            onClick={addPresenceSubscriptionCharges}>
                                            <PreIcon primColor icon="add">
                                                Add Charges
                                            </PreIcon>
                                        </Button>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="border-b border-grey-500 my-2 mt-2"></div>

                    {/* Show warning when total discount exceeds subscription fee */}
                    {(() => {
                        const discountInfo = getEffectiveDiscountInfo()

                        if (
                            discountInfo.isOverDiscounted &&
                            subscription.presence_discounts &&
                            subscription.presence_discounts.length > 0
                        ) {
                            return (
                                <div className="mb-3 mt-3 p-2 bg-tertiary-50 border border-tertiary-200 rounded-md">
                                    <div className="grid grid-cols-[auto_1fr] gap-3 items-center text-tertiary-800 text-md">
                                        <Icon name="warning" orange className="mt-0.5" />
                                        <div>
                                            <span className="font-bold">Warning:</span> The total
                                            discount (€
                                            <span className="font-bold">
                                                {discountInfo.originalDiscount.toFixed(2)}
                                            </span>
                                            ) exceeds the subscription fee (€
                                            <span className="font-bold">
                                                {discountInfo.baseAmount.toFixed(2)}
                                            </span>
                                            ). The effective discount is capped at €
                                            <span className="font-bold">
                                                {discountInfo.baseAmount.toFixed(2)}
                                            </span>
                                            , making the subscription free.
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                        return null
                    })()}

                    {/* Discounts Content */}
                    <>
                        <PresenceDiscountList
                            discounts={subscription.presence_discounts || []}
                            onDeleteDiscount={deleteDiscount}
                            getEffectiveBillingMonth={getEffectiveBillingMonth}
                        />

                        {subscription.status === SUBSCRIPTION_STATUS.ACTIVE && (
                            <Button name="text" onClick={() => addDiscount()}>
                                <PreIcon primColor icon="add">
                                    Add Discount
                                </PreIcon>
                            </Button>
                        )}
                        {hasPendingSubscription && (
                            <Button name="text" onClick={addRolloverDiscount}>
                                <PreIcon primColor icon="add">
                                    Add Discount Offer
                                </PreIcon>
                            </Button>
                        )}
                    </>
                </div>
            )
        }

        // Fallback - should not reach here
        return <p>No subscription data available</p>
    }

    return (
        <div className="relative">
            <Divider sm />
            {renderContent()}
        </div>
    )
}

export default PresenceSubscriptionSettings
